/* eslint-disable jsx-a11y/anchor-is-valid */
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import { ICartItemInfo, ISession } from '../../../models/pro-sites';
import { IPatient } from '../../../models/prosites';
import {
    CartType,
    cartItemTypeIdsWithoutProductDetailsPage,
} from '../../../models/constants';
import { Quantity } from './quantity';
import WarrantyText from '../../common/warranty-text';
import { isMiracleEar } from '../../../hooks/miracle-ear-hook';
import {
    useHandleToggleFavorite,
    useIsFavorite,
} from '../../../hooks/use-is-favorite';

interface ICartItemProps {
    item: ICartItemInfo;
    maxQuantity: number;
    type: CartType;
    session: ISession;
    addTopLine: boolean;
    addBottomLine: boolean;
    onMoveItem: (lineItemId: string, isPatient: boolean) => void;
    onDeleteItem: (itemId: string) => void;
    onUpdateItemQuantity: (itemId: string, quantity: number) => Promise<void>;
    setDisableProceedToCheckout: React.Dispatch<React.SetStateAction<boolean>>;
}

const CartItem = ({
    item,
    maxQuantity,
    session,
    type,
    addTopLine,
    addBottomLine,
    onMoveItem,
    onDeleteItem,
    onUpdateItemQuantity,
    setDisableProceedToCheckout,
}: ICartItemProps) => {
    const handleToggleFavorite = useHandleToggleFavorite();
    const isFavorite = useIsFavorite();

    const getPatientDetails = (item: ICartItemInfo) => {
        const patient = item.patient;
        return patient ? (
            <>
                {item.orderType === 'Stock Order' ? (
                    <div id="cart-stock-order">
                        <span>
                            <FormattedMessage id="Stock_Order" />
                        </span>
                    </div>
                ) : (
                    <div id="patient-order-div">
                        <span>
                            <FormattedMessage id="Patient_Order" />
                        </span>
                    </div>
                )}

                <div>
                    {isMiracleEar(session.siteNumber) ? (
                        <span id="patient-name-span">{patient.lastName}</span>
                    ) : (
                        <>
                            {patient.lastName && (
                                <span id="patient-name-span">
                                    {patient.lastName +
                                        ', ' +
                                        patient.firstName}
                                </span>
                            )}
                        </>
                    )}
                </div>
                <div>
                    {patient.ageRange && (
                        <span id="patient-age-range-span">
                            <FormattedMessage id="Age_Range" />:{' '}
                            {patient.ageRange}
                        </span>
                    )}
                </div>
                {patient.agreement && patient.agreementType && (
                    <div>
                        <span>{item.agreement_Type}</span>
                    </div>
                )}
                {patient.agreement && patient.agreementState && (
                    <div>
                        <span>{item.agreement_State}</span>
                    </div>
                )}
                {patient.agreement && patient.agreementReference && (
                    <div>
                        <span>{item.agreement_Reference}</span>
                    </div>
                )}
                {patient.agreement && patient.agreementInfo && (
                    <div>
                        <span>{item.agreement_Info}</span>
                    </div>
                )}
            </>
        ) : (
            <div id="cart-stock-order">
                <span>
                    <FormattedMessage id="Stock_Order" />
                </span>
            </div>
        );
    };

    return (
        <>
            {addTopLine && <hr />}
            <div
                className="row"
                style={{
                    paddingTop: '10px',
                }}
            >
                <div className="col-md-10">
                    <div className="row">
                        <div className="col-md-2">
                            {cartItemTypeIdsWithoutProductDetailsPage.includes(
                                item.cartItemTypeId!
                            ) ? (
                                <img
                                    alt={item.description}
                                    style={{
                                        width: '100px',
                                        height: '100px',
                                        paddingTop: '5px',
                                    }}
                                    src={item.imageUrl}
                                />
                            ) : (
                                <Link to={item.url.replace('/prosite', '')}>
                                    <img
                                        alt={item.description}
                                        style={{
                                            width: '100px',
                                            height: '100px',
                                            paddingTop: '5px',
                                        }}
                                        src={item.imageUrl}
                                    />
                                </Link>
                            )}
                        </div>
                        <div className="col-md-4">
                            {cartItemTypeIdsWithoutProductDetailsPage.includes(
                                item.cartItemTypeId!
                            ) ? (
                                <span
                                    style={{ fontWeight: 'bold' }}
                                    id="cart-item-description-span"
                                >
                                    {item.description}
                                </span>
                            ) : (
                                <Link to={item.url.replace('/prosite', '')}>
                                    <span>{item.description}</span>
                                </Link>
                            )}
                            <div id="cart-partnumber">{item.partNumber}</div>
                            {item.color && (
                                <div>
                                    <FormattedMessage
                                        id={item.color}
                                        defaultMessage={item.color}
                                    />
                                </div>
                            )}
                            {item.options &&
                                item.options.map((option, i) => (
                                    <div key={i} id={option.id.toString()}>
                                        {option.side_URL && (
                                            <img
                                                alt={option.name}
                                                src={option.side_URL}
                                            />
                                        )}
                                        {option.name}
                                    </div>
                                ))}
                            <WarrantyText warranty={item.warranty} />
                        </div>
                        <div className="col-md-4">
                            {item.itemID === item.lineItemID &&
                                getPatientDetails(item)}
                        </div>
                        <div
                            className="col-md-2"
                            style={{
                                paddingLeft: '0px',
                            }}
                        >
                            <Quantity
                                item={item}
                                maxQuantity={maxQuantity}
                                onUpdateItemQuantity={onUpdateItemQuantity}
                                setDisableProceedToCheckout={
                                    setDisableProceedToCheckout
                                }
                            />
                        </div>
                    </div>
                </div>
                {item.itemID === item.lineItemID && (
                    <div className="col-md-2">
                        {!cartItemTypeIdsWithoutProductDetailsPage.includes(
                            item.cartItemTypeId!
                        ) && (
                            <div className="row">
                                <div
                                    className="col-md-12"
                                    style={{ marginBottom: '5px' }}
                                >
                                    <Link
                                        to={item.url.replace('/prosite', '')}
                                        id="cart-item-edit-btn"
                                    >
                                        <span>
                                            <FormattedMessage id="Edit" />
                                        </span>
                                    </Link>
                                </div>
                            </div>
                        )}
                        <div className="row">
                            <div
                                className="col-md-12"
                                style={{ marginBottom: '5px' }}
                            >
                                <a
                                    id="cart-item-delete-btn"
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => onDeleteItem(item.itemID)}
                                >
                                    <span>
                                        <FormattedMessage id="Delete" />
                                    </span>
                                </a>
                            </div>
                        </div>
                        {(cartItemTypeIdsWithoutProductDetailsPage.includes(
                            item.cartItemTypeId!
                        ) ||
                            item.baseProductId) && (
                            <div className="row">
                                <div
                                    className="col-md-12"
                                    style={{ marginBottom: '5px' }}
                                >
                                    <a
                                        id="cart-item-favorite-btn"
                                        style={{ cursor: 'pointer' }}
                                        onClick={() =>
                                            handleToggleFavorite(
                                                item.baseProductId
                                                    ? item.baseProductId
                                                    : item.itemGuid
                                            )
                                        }
                                    >
                                        {isFavorite(
                                            item.baseProductId
                                                ? item.baseProductId
                                                : item.itemGuid
                                        ) ? (
                                            <span>
                                                <FormattedMessage id="Remove_Favorite" />
                                            </span>
                                        ) : (
                                            <span>
                                                <FormattedMessage id="Add_Favorite" />
                                            </span>
                                        )}
                                    </a>
                                </div>
                            </div>
                        )}
                        <div className="row">
                            <div
                                className="col-md-12"
                                style={{ marginBottom: '5px' }}
                            >
                                <a
                                    id="cart-save-or-move-btn"
                                    style={{ cursor: 'pointer' }}
                                    onClick={() =>
                                        onMoveItem(
                                            item.lineItemID,
                                            item.isPatient
                                        )
                                    }
                                >
                                    {type === CartType.Primary ? (
                                        <span>
                                            <FormattedMessage id="Save_for_Later_2" />
                                        </span>
                                    ) : (
                                        <span>
                                            <FormattedMessage id="Move_to_Cart_2" />
                                        </span>
                                    )}
                                </a>
                            </div>
                        </div>
                    </div>
                )}
            </div>
            {addBottomLine && <hr />}
        </>
    );
};

export default CartItem;
